<script setup lang="ts">
import { faShip } from '@fortawesome/pro-regular-svg-icons';
import {
    type ShippingPricesQuery,
    ShipReserveDocument,
} from '@schema/directus';
import { TripType } from '@utils/trip';

const emit = defineEmits<{
    close: [successful: boolean];
}>();
const shipForm = useShipForm();
const { currentShippingPrices } = moduleToRefs(shipForm);
const authStore = useAuthStore();
const { userInfo } = storeToRefs(authStore);
const fullName = ref('');
const fullNameValid = ref(true);
const phoneNumber = ref('');
const phoneNumberValid = ref(true);
const showRules = ref(false);
const originDestination = computed(
    () => `${shipForm.originName}-${shipForm.destinationName}`
);
const departureDate = computed(() =>
    !shipForm.departureDate
        ? ''
        : dayConfig
              .js(shipForm.departureDate)
              .format(formatter({ format: 'long', includeWeekday: true }))
);
const departureJalaliDate = computed(() =>
    !shipForm.departureDate
        ? ''
        : dayConfig
              .jalali(shipForm.departureDate)
              .format(formatter({ format: 'long', includeWeekday: true }))
);
const returnDate = computed(() => {
    if (!shipForm.returnDate || shipForm.tripType === TripType.OneWay) {
        return undefined;
    }
    return dayConfig
        .js(shipForm.returnDate)
        .format(formatter({ format: 'long', includeWeekday: true }));
});
const returnJalaliDate = computed(() => {
    if (!shipForm.returnDate || shipForm.tripType === TripType.OneWay) {
        return undefined;
    }
    return dayConfig
        .jalali(shipForm.returnDate)
        .format(formatter({ format: 'long', includeWeekday: true }));
});
const dateText = computed(
    () =>
        `${departureDate.value}${
            returnDate.value ? ` به ${returnDate.value}` : ''
        }`
);
const currentShippingPrice = computed<
    ShippingPricesQuery['ShippingPrices'][number] | null
>(() => {
    const roundedPrice = currentShippingPrices.value.find(s => s.RoundTrip);
    const onWayPrice = currentShippingPrices.value.find(s => !s.RoundTrip);
    if (shipForm.tripType === TripType.RoundTrip && roundedPrice) {
        return roundedPrice;
    } else if (onWayPrice) {
        return onWayPrice;
    } else {
        return null;
    }
});
const departureDescription = computed(
    () => currentShippingPrice.value?.DepartureDescription ?? ''
);
const classTitle = computed(() => {
    switch (currentShippingPrice.value?.Class) {
        case 'GRADE_ONE':
            return 'درجه یک';
        case 'GRADE_TWO':
        default:
            return 'درجه دو';
    }
});
const priceDescription = computed(() => {
    if (!currentShippingPrice.value) {
        return 'مسیر مورد نظر موجود نمی‌باشد.';
    } else {
        return formatMoney(currentShippingPrice.value.Price, {
            unitClassName: 'toman',
        });
    }
});
const tripTypeText = computed(() =>
    currentShippingPrice.value?.RoundTrip ? 'رفت و برگشت' : 'یک‌طرفه'
);
const { mutate } = useMutation(ShipReserveDocument);
const onSubmit = async () => {
    validateName();
    validatePhone();
    if (!fullNameValid.value || !phoneNumberValid.value) {
        showRules.value = false;
        return;
    }
    try {
        await mutate({
            FullName: fullName.value,
            PhoneNumber: phoneNumber.value,
            Origin: shipForm.originCode!,
            Destination: shipForm.destinationCode!,
            DepartureDate: departureJalaliDate.value,
            ReturnDate: returnJalaliDate.value,
            AdultCount: shipForm.adultCount,
            ChildCount: shipForm.childCount,
            InfantCount: shipForm.infantCount,
            CarCount: shipForm.carCount,
        });
    } catch (e) {
        reportException(e);
    } finally {
        emit('close', true);
    }
};
const validatePhone = (): boolean =>
    (phoneNumberValid.value =
        !!phoneNumber.value &&
        phoneNumber.value.length >= 11 &&
        /^09\d{9}$/.test(convertPersianNumbers(phoneNumber.value || '')));
const validateName = (): boolean => (fullNameValid.value = !!fullName.value);
watch(
    userInfo,
    info => {
        if (info) {
            fullName.value = `${info.title} ${info.lastName}`;
            phoneNumber.value = info.mobile || '';
        }
    },
    { immediate: true }
);
</script>

<template>
    <mr-modal
        title="درخواست رزرو کشتی"
        width="fit-content"
        @close="emit('close', false)"
    >
        <header class="tabs-header">
            <button
                class="tab"
                :class="{ selected: !showRules }"
                @click="showRules = false"
            >
                اطلاعات سفر
            </button>
            <button
                class="tab"
                :class="{ selected: showRules }"
                @click="showRules = true"
            >
                قوانین و مقررات
            </button>
        </header>
        <div v-show="!showRules" class="ship-reserve-container">
            <div class="trip-info-container">
                <div class="info-list">
                    <mr-icon
                        :icon="faShip"
                        size="large"
                        color="var(--blue-400)"
                    />
                    <div class="title">
                        {{ originDestination }}
                    </div>
                    <div class="price-description" v-html="priceDescription" />
                </div>
                <div class="labels">
                    <div class="title">
                        {{ dateText }}
                    </div>
                    <div class="title">
                        {{ classTitle }}
                    </div>
                    <div class="title">
                        {{ tripTypeText }}
                    </div>
                </div>
            </div>
            <div v-if="departureDescription" class="departure-description">
                {{ departureDescription }}
            </div>
            <mr-input
                v-model="fullName"
                placeholder="نام و نام خانوادگی را وارد کنید"
                :invalid="!fullNameValid"
                :validation="validateName"
                required
            />
            <mr-input
                v-model="phoneNumber"
                placeholder="شماره تماس را وارد کنید"
                :invalid="!phoneNumberValid"
                :max-length="11"
                :validation="validatePhone"
                required
            />
        </div>
        <div v-show="showRules" class="ship-reserve-container rules">
            - حضور در ترمینال مسافری دریایی 4 ساعت قبل از سفر الزامیست.<br />
            - مدارک لازم جهت سفر <br />
            1- پاسپورت<br />
            2- تست pcr<br />
            3- بلیط مسافرین <br />
            4- متناسب با شرایط بعضی کشورها اقامت، ویزا یا بلیط برگشت نیز باید
            تهیه بشود<br />
            نکته مهم: همراه داشتن تست pcr که از مسافر/مسافرین حداکثر 48 ساعت قبل
            حرکت گرفته شده باشد برای سفر الزامیست.<br />
            - خواهشمند است حداکثر تا یک روز قبل از سفر عوارض خروج از کشور را
            پرداخت کرده و فیش واریزی را همراه خود داشته باشید <br />
            - میزان بار مجاز سفر بسته به مسیر مورد نظر متفاوت است<br />
            - دقت کنید در صورتیکه بار به کشتی تحویل داده شود و به دلیل مشکل
            روادید مسافر موفق به انجام سفر شود بلیط غیر قابل استرداد است.
            <br />
            - طبق قوانین کشتیرانی استرداد بلیط همواره با جریمه انجام می‌شود، مگر
            به غیر از موارد تغییر برنامه سفر کشتی از سوی کشتیرانی به دلیل شرایط
            جوی آب و هوا، که بدون لحاظ جریمه مسترد می‌شود.<br />
            - لازم به ذکر است اطلاعات مسافرین دقیقا مطابق گذرنامه ثبت شده باشد و
            عدم تطبیق آن بر عهده خود مسافر می‌باشد. <br />
            - حداقل اعتبار گذرنامه از تاریخ بازگشت از سفر، شش ماه می‌باشد.<br />
            - زمان ورود و مدت سفر به صورت تخمینی بوده و مِستربلیط مسئولیتی در
            قبال آن ندارد.<br />
        </div>
        <template #footer>
            <mr-button id="reserve-ship-button" full-width @click="onSubmit">
                تایید
            </mr-button>
        </template>
    </mr-modal>
</template>

<style lang="scss" scoped>
.tabs-header {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 18px;
    width: calc(100% - 40px);
    padding-inline: 20px;
    margin: -12px auto 16px auto;
    border-bottom: 1px solid usePalette($p-cool-gray, $p-200);
    @include mobile-only {
        @include no-scrollbar;
        padding: 0 16px 0 0;
        overflow-x: scroll;
    }
    .tab {
        all: unset;
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 144px;
        @include tg-m($color: usePalette($p-cool-gray, 600));
        padding: 16px 0 5px 0;
        margin-left: 4px;
        border-bottom-width: 3px;
        border-bottom-color: transparent;
        border-bottom-style: solid;
        cursor: pointer;
        white-space: nowrap;
        &.selected {
            color: usePalette();
            border-bottom-color: usePalette();
        }
    }
}
.ship-reserve-container {
    padding: 0 20px 0 20px;
    width: 550px;
    &.rules {
        @include tg-m;
    }
    @include mobile-only {
        padding: 8px 20px 12px 20px;
        width: 100%;
    }
}
.trip-info-container {
    background: #f0f4f7;
    border-radius: 12px;
    padding: 8px;
    .info-list {
        display: flex;
        align-items: center;
        flex-wrap: wrap;

        .title {
            @include tg-m($w7);
            margin-right: 8px;
        }

        .price-description {
            flex-shrink: 0;
            @include tg-display4($w7, usePalette($p-blue, $p-500));
            margin-right: auto;
            &:deep() {
                .toman {
                    @include tg-xl($w5, usePalette($p-cool-gray, $p-600));
                }
            }
        }
    }
    .labels {
        display: flex;
        align-items: center;
        flex-wrap: wrap;

        .title {
            display: flex;
            align-items: center;
            justify-content: center;
            @include tg-m($w5);
            margin-inline: 4px;
            white-space: nowrap;
            &:not(:first-child) {
                &:before {
                    content: '  ';
                    width: 4px;
                    height: 4px;
                    background: black;
                    border-radius: 100%;
                    margin-left: 8px;
                }
            }
        }
    }
}
.departure-description {
    @include tg-m($w5);
    margin: 16px 0;
}
</style>
